import type {
  Product,
  ProductIdentifierResolution,
} from "@/types/domain";

export const MAX_SCAN_LENGTH = 100;

export type InventoryScanProduct = Pick<
  Product,
  | "id"
  | "sku"
  | "description_officielle"
  | "emplacement"
  | "quantite_physique"
  | "quantite_reservee"
  | "quantite_disponible"
  | "quantite_en_commande"
  | "actif"
>;

export type ScanResolution =
  | { kind: "empty" }
  | { kind: "invalid"; scan: string }
  | { kind: "not_found"; scan: string }
  | { kind: "ambiguous"; scan: string; matches: InventoryScanProduct[] }
  | { kind: "inactive"; scan: string; product?: InventoryScanProduct }
  | { kind: "error"; scan: string; message: string }
  | { kind: "found"; scan: string; product: InventoryScanProduct };

export type RemoteResolutionDecision =
  | { kind: "fallback" }
  | { kind: "resolved"; resolution: ScanResolution };

export function normalizeScanInput(value: string): string {
  return value.trim();
}

export function isDuplicateScan(value: string, previousScan: string | null): boolean {
  const normalized = normalizeScanInput(value);
  return normalized !== "" && normalized === previousScan;
}

export function resolveInventoryScan(
  value: string,
  products: InventoryScanProduct[]
): ScanResolution {
  const scan = normalizeScanInput(value);

  if (!scan) {
    return { kind: "empty" };
  }

  if (scan.length > MAX_SCAN_LENGTH) {
    return { kind: "invalid", scan };
  }

  // MVP compatibility mode: exact Rockfall SKU only. This deliberately does
  // not infer a barcode, normalize case, or use descriptions as identifiers.
  const matches = products.filter((product) => product.sku === scan);

  if (matches.length === 0) {
    return { kind: "not_found", scan };
  }

  if (matches.length > 1) {
    return { kind: "ambiguous", scan, matches };
  }

  const product = matches[0];
  return product.actif
    ? { kind: "found", scan, product }
    : { kind: "inactive", scan, product };
}

export function productFromIdentifierResolution(
  response: ProductIdentifierResolution
): InventoryScanProduct {
  return {
    id: response.produit_id,
    sku: response.sku_rockfall,
    description_officielle: response.description,
    emplacement: response.emplacement,
    quantite_physique: response.quantite_physique,
    quantite_reservee: response.quantite_reservee,
    quantite_disponible: response.quantite_disponible,
    quantite_en_commande: response.quantite_en_commande,
    actif: response.statut === "ACTIF",
  };
}

export function decideRemoteResolutionError(
  error: unknown,
  scan: string
): RemoteResolutionDecision {
  const failure = error as {
    category?: string;
    status?: number;
    detail?: unknown;
    message?: string;
  };
  const detail = typeof failure.detail === "string" ? failure.detail : "";

  if (
    ["configuration", "timeout", "network"].includes(failure.category ?? "") ||
    (typeof failure.status === "number" && failure.status >= 500) ||
    (failure.status === 404 && detail !== "Identifiant produit introuvable.")
  ) {
    return { kind: "fallback" };
  }

  if (failure.status === 404) {
    return {
      kind: "resolved",
      resolution: { kind: "not_found", scan },
    };
  }

  if (failure.status === 409 && detail.includes("ambigu")) {
    return {
      kind: "resolved",
      resolution: { kind: "ambiguous", scan, matches: [] },
    };
  }

  if (failure.status === 409 && detail.includes("inactif")) {
    return {
      kind: "resolved",
      resolution: { kind: "inactive", scan },
    };
  }

  return {
    kind: "resolved",
    resolution: {
      kind: "error",
      scan,
      message: failure.message || "Impossible de résoudre cet identifiant.",
    },
  };
}

export function parsePreparedQuantity(value: string): number | null {
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) {
    return null;
  }

  const quantity = Number(normalized);
  return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
}
